export type RouteAccessLevel =
  | 'public'
  | 'authenticated'
  | 'staff'
  | 'command-center';

export interface RouteAccessDecision {
  readonly allowed: boolean;
  readonly destination:
    | '/login'
    | '/app'
    | null;
  readonly reason:
    | 'PUBLIC_ROUTE'
    | 'AUTHENTICATED_ACCESS'
    | 'STAFF_ACCESS'
    | 'COMMAND_CENTER_ACCESS'
    | 'LOGIN_REQUIRED'
    | 'STAFF_REQUIRED'
    | 'COMMAND_CENTER_REQUIRED';
}

const normalizeInternalPath = (
  raw: string | null | undefined,
): string | null => {
  if (!raw) return null;

  const value = raw.trim();

  if (
    !value.startsWith('/') ||
    value.startsWith('//')
  ) {
    return null;
  }

  return value;
};

export function resolveRouteAccess(
  level: RouteAccessLevel,
  authenticated: boolean,
  staff: boolean,
  commandCenterAuthorized = false,
): RouteAccessDecision {
  if (level === 'public') {
    return Object.freeze({
      allowed: true,
      destination: null,
      reason: 'PUBLIC_ROUTE',
    });
  }

  if (!authenticated) {
    return Object.freeze({
      allowed: false,
      destination: '/login',
      reason: 'LOGIN_REQUIRED',
    });
  }

  if (
    level === 'command-center' &&
    !commandCenterAuthorized
  ) {
    return Object.freeze({
      allowed: false,
      destination: '/app',
      reason: 'COMMAND_CENTER_REQUIRED',
    });
  }

  if (level === 'staff' && !staff) {
    return Object.freeze({
      allowed: false,
      destination: '/app',
      reason: 'STAFF_REQUIRED',
    });
  }

  return Object.freeze({
    allowed: true,
    destination: null,
    reason:
      level === 'command-center'
        ? 'COMMAND_CENTER_ACCESS'
        : level === 'staff'
          ? 'STAFF_ACCESS'
          : 'AUTHENTICATED_ACCESS',
  });
}

export function resolveApprovedLoginContinuation(
  raw: string | null | undefined,
  staff: boolean,
  commandCenterAuthorized = false,
): string {
  const internal =
    normalizeInternalPath(raw);

  if (!internal) {
    return '/app';
  }

  const url = new URL(
    internal,
    'https://kinscircleapp.com',
  );

  const aliasMap: Record<string, string> = {
    '/dashboard': '/app',
    '/account': '/settings',
    '/membership': '/settings',
    '/governor': '/governor-command-center',
    '/governor-command':
      '/governor-command-center',
    '/governor-registry':
      '/governor-command-center',
    '/governor-executive':
      '/governor-command-center',
  };

  const pathname =
    aliasMap[url.pathname] ??
    url.pathname;

  const authenticatedPaths = new Set([
    '/app',
    '/settings',
    '/native-call',
    '/graph-intelligence',
  ]);

  const commandCenterPaths = new Set([
    '/governor-command-center',
    '/system-health',
    '/admin/health',
  ]);

  const staffPaths = new Set([
    '/admin/ai',
    '/admin/semantic',
    '/admin/legacy',
    '/admin/preservation',
    '/admin/integrity',
    '/admin/keys',
    '/secure-keys',
    '/settings/email',
    '/v2',
    '/kc-v2',
  ]);

  const authenticatedAllowed =
    authenticatedPaths.has(pathname);

  const staffAllowed =
    staff && staffPaths.has(pathname);

  const commandCenterAllowed =
    commandCenterAuthorized &&
    commandCenterPaths.has(pathname);

  if (
    !authenticatedAllowed &&
    !staffAllowed &&
    !commandCenterAllowed
  ) {
    return '/app';
  }

  return (
    pathname +
    url.search +
    url.hash
  );
}

export function buildLoginDestination(
  pathname: string,
  search = '',
  hash = '',
): string {
  const next =
    pathname + search + hash;

  return (
    '/login?next=' +
    encodeURIComponent(next)
  );
}
